/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import BASE_QUERY from '@/features/baseQuery';
import { chunk } from 'lodash';

const CLASS_OF_RISK_COLLECTION = 'classesOfRisk';
const CLASS_OF_RISK_GUIDANCE_COLLECTION = 'classOfRiskGuidance';

const CHIP_COLOURS = [
	'#CFEEDD', // mint green (reference brightness)
	'#E7D9FF', // soft violet
	'#FFE1C6', // peach
	'#D6ECFF', // sky blue
	'#FAD6E8', // rose
	'#E2F2C2', // yellow-green
	'#E8D4F8', // lavender
	'#FFE9B8', // warm pastel yellow
	'#CDEBFF', // blue mist
	'#F6D1C1', // coral peach
	'#D7F0E8', // aqua mint
	'#EBD9C7', // sand
	'#E0DFFF', // periwinkle
	'#FFE3ED', // blush pink
	'#DFF3C6', // fresh green
	'#E6D1FF', // lilac
	'#FFEFD0', // cream yellow
	'#CDE8E6', // teal pastel
	'#F1D8E4', // dusty rose
	'#E4F1D9', // soft pistachio
];

const getClassesOfRiskByConstraints = async ({
	brokerGroupIds = [],
	constraints: baseConstraints = [],
	clientType,
}) => {
	const collectionRef = collection(firestore, CLASS_OF_RISK_COLLECTION);

	const brokerGroupCollection = collection(firestore, 'brokerGroup');
	const brokerGroups = brokerGroupIds.map((id) => doc(brokerGroupCollection, id));

	// console.log('BROKER GROUPS', brokerGroups);

	const docs = [];
	if (brokerGroups.length > 0) {
		const chunks = chunk(brokerGroups, 30);
		for (const brokerGroupChunks of chunks) {
			const constraints = [
				where('brokerGroups', 'array-contains-any', brokerGroupChunks),
				...baseConstraints,
			];
			const q = query(collectionRef, ...constraints);
			const snapshot = await getDocs(q);
			docs.push(
				...snapshot.docs.filter((d) => {
					return (
						!clientType || (d.data().clientTypes ?? []).some((t) => t === clientType)
					);
				}),
			);
		}
	} else {
		const constraints = [
			...baseConstraints,
			...(clientType ? [where('clientTypes', 'array-contains-any', [clientType])] : []),
		];
		const q = query(collectionRef, ...constraints);
		const snapshot = await getDocs(q);
		docs.push(...snapshot.docs.filter((d) => (d.data().brokerGroups ?? []).length === 0));
	}
	return docs;
};

const parseClassOfRiskDoc = async (classOfRiskDoc) => {
	const { parentRef, brokerGroups, ...data } = classOfRiskDoc.data();
	const parentDoc = parentRef ? await getDoc(parentRef) : null;
	const brokerGroupIds = (brokerGroups ?? []).map((r) => r.id);
	return {
		id: classOfRiskDoc.id,
		...JSON.parse(JSON.stringify(data)),
		...(parentDoc && { parentDoc: JSON.parse(JSON.stringify(parentDoc)) }),
		brokerGroupIds,
	};
};

const getGuidanceByClassOfRisk = async (classOfRisk, brokerGroupIds = []) => {
	if (!classOfRisk) return null;

	const collectionRef = collection(firestore, CLASS_OF_RISK_GUIDANCE_COLLECTION);
	const brokerGroupCollection = collection(firestore, 'brokerGroup');
	const brokerGroups = brokerGroupIds.map((id) => doc(brokerGroupCollection, id));

	const constraints = [where('classOfRisk', '==', classOfRisk)];
	if (brokerGroups.length > 0) {
		constraints.push(where('brokerGroups', 'array-contains-any', brokerGroups));
	}

	const q = query(collectionRef, ...constraints);
	const snapshot = await getDocs(q);

	return snapshot.empty ? null : snapshot.docs[0];
};

// Creating an API service using RTK Query
export const classesOfRiskApi = createApi({
	reducerPath: 'classesOfRiskApi',
	tagTypes: ['CLASSES_OF_RISK'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getClassesOfRisk: builder.query({
			providesTags: (result, error, arg) => [
				{
					type: 'CLASSES_OF_RISK',
					id: `${arg?.countryCode}${arg?.classOfRisk ? `-${arg.classOfRisk}` : ''}${
						arg?.brokerGroupIds ? `-${arg.brokerGroupIds.join('_')}` : ''
					}`,
				},
			],
			queryFn: async (
				{ countryCode, classOfRisk, isParent, clientType, brokerGroupIds = [] },
				{ getState, dispatch },
			) => {
				const baseConstraints = [
					...(brokerGroupIds.length === 0
						? [where('countryCode', '==', countryCode)]
						: []),
					...(classOfRisk ? [where('parent', '==', classOfRisk)] : []),
					...(isParent ? [where('isParent', '==', true)] : []),
				];

				let docs = await getClassesOfRiskByConstraints({
					brokerGroupIds,
					constraints: baseConstraints,
					clientType,
				});
				// We either return entirely custom classes of risk for the broker group,
				// or we return the standard classes of risk for the countryCode
				if (docs.length === 0 && brokerGroupIds.length > 0) {
					docs = await getClassesOfRiskByConstraints({
						constraints: [where('countryCode', '==', countryCode), ...baseConstraints],
						clientType,
					});
				}

				const response = await Promise.all(
					docs.map(async (d) => await parseClassOfRiskDoc(d)),
				);
				const sortedResponse = response
					.slice()
					.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
				console.log('getClassesOfRisk docs', sortedResponse);
				return {
					data: sortedResponse,
				};
			},
		}),
		getClassOfRisk: builder.query({
			providesTags: (result, error, arg) => [
				{
					type: 'CLASSES_OF_RISK',
					id: `${arg?.countryCode}${arg?.classOfRisk ? `-${arg.classOfRisk}` : ''}${
						arg?.brokerGroupIds ? `-${arg.brokerGroupIds.join('_')}` : ''
					}`,
				},
			],
			queryFn: async (
				{ countryCode, classOfRisk, brokerGroupIds },
				{ getState, dispatch },
			) => {
				const baseConstraints = [
					...(brokerGroupIds.length === 0
						? [where('countryCode', '==', countryCode)]
						: []),
					where('classOfRisk', '==', classOfRisk),
				];
				let docs = await getClassesOfRiskByConstraints({
					brokerGroupIds,
					constraints: baseConstraints,
				});
				// We either return entirely custom classes of risk for the broker group,
				// or we return the standard classes of risk for the countryCode
				if (docs.length === 0 && brokerGroupIds.length > 0) {
					docs = await getClassesOfRiskByConstraints({
						constraints: [where('countryCode', '==', countryCode), ...baseConstraints],
					});
				}

				// const resDoc = snapshot.docs[0];
				const resDoc = docs[0];
				if (!resDoc) return { data: null };

				const response = await parseClassOfRiskDoc(resDoc);

				console.log('getClassOfRisk response', response);
				return {
					data: response,
				};
			},
		}),
		getGuidanceByClassOfRisk: builder.query({
			providesTags: (result, error, arg) => [
				{
					type: 'CLASSES_OF_RISK',
					id: `guidance-${arg?.classOfRisk}`,
				},
			],
			queryFn: async ({ classOfRisk, brokerGroupIds = [] }, { getState, dispatch }) => {
				try {
					const guidanceDoc = await getGuidanceByClassOfRisk(classOfRisk, brokerGroupIds);

					if (!guidanceDoc) {
						return { data: null };
					}

					const data = guidanceDoc.data();
					const parsedGuidance = {
						id: guidanceDoc.id,
						classOfRisk: data.classOfRisk,
						description: data.description,
						brokerGroupIds: (data.brokerGroups ?? []).map((r) => r.id),
					};

					return { data: parsedGuidance };
				} catch (error) {
					console.error('Error fetching guidance:', error);
					return { error: { status: 500, message: 'Error loading guidance content' } };
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetClassesOfRiskQuery,
	useLazyGetClassesOfRiskQuery,
	useGetClassOfRiskQuery,
	useGetGuidanceByClassOfRiskQuery,
} = classesOfRiskApi;
